import type { Ref } from "react";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import type { Item } from "../../../../api/menu";
import {
  getItemName,
  getItemUnitAndSize,
} from "../../../../utils/translations";
import { useTranslation } from "react-i18next";
import { isLunchItem, lunchRoomLeft } from "../../../../lib/lunchCatalog";
import { LunchCount, LunchTag } from "../../lunch/LunchTag";
import { highlightMatch as highlight, highlightPluCode } from "./highlight";
import {
  applyBillPercentToPrice,
  formatSignedBillPercent,
} from "../billDiscount";
import { tintBackground } from "./catalogColors";
import { CatalogQtyStepper } from "./CatalogQtyStepper";

interface ItemRowProps {
  item: Item;
  selected?: boolean;
  onSelect?: (itemId: number) => void;
  searchTerm?: string;
  quantity?: number;
  onQuantityChange?: (itemId: number, quantity: number) => void;
  // Мобилен 2-редов вариант — име + цена под него
  stacked?: boolean;
  // Знаков процент по сметката (клиент с отстъпка/надбавка) — цената се
  // показва като ~~стара~~ ±X% нова; 0 = без корекция
  billDiscountPercent?: number;
  // Реф към DOM реда — ползва се от ItemCatalog за keyboard позиции и scroll-into-view
  ref?: Ref<HTMLDivElement>;
  // Цвят на реда по настройката „оцветяване" (станция/група); null = без фон
  tint?: string | null;
  // Колоната „Станция" (само десктоп): името и цветът на първата станция
  station?: { name: string; color: string | null } | null;
  // Моливът в степера (десктоп) — отваря EditModifiersModal за продукта
  onEdit?: (itemId: number) => void;
}

/**
 * Продуктов ред в каталога. Десктоп: име · степер (− N + | молив, само при
 * маркирано количество) · грамаж · станция · цена. Телефон (stacked): име и
 * цена под него · степер − N +.
 * Клик върху реда = селекция + добавя +1 (като „+"); степерът променя количеството.
 */
export const ItemRow = ({
  item,
  selected = false,
  onSelect,
  searchTerm = "",
  quantity = 0,
  onQuantityChange,
  stacked = false,
  billDiscountPercent = 0,
  ref,
  tint = null,
  station = null,
  onEdit,
}: ItemRowProps) => {
  const { i18n } = useTranslation();
  const itemName = getItemName(item.itemLangs, i18n.language, item.slug);
  const unitAndSize = getItemUnitAndSize(item.itemLangs, i18n.language);
  const hasQty = quantity > 0;
  // Обеден продукт с дневен лимит: колко още могат да се добавят (без
  // кошницата); 0 → сив и „Изчерпано". Ограничението е в Ordering.
  const lunchLeft = lunchRoomLeft(item, quantity);
  const lunchRemaining = Number.isFinite(lunchLeft) ? lunchLeft : null;
  const lunchSoldOut = lunchRemaining === 0 && !hasQty;
  // Заключената цена не се коригира от клиентския процент (както в BE)
  const showAdjustedPrice = billDiscountPercent !== 0 && !item.lockedPrice;
  const adjustedPrice = applyBillPercentToPrice(
    item.price,
    billDiscountPercent,
  );

  const handleClick = () => onSelect?.(item.id);

  // Споделен highlight (multi-word) — виж menu/highlight.tsx
  const highlightMatch = (text: string) => highlight(text, searchTerm);

  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    onQuantityChange?.(item.id, quantity + 1);
    (e.currentTarget as HTMLElement).blur();
  };

  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    onQuantityChange?.(item.id, Math.max(0, quantity - 1));
    (e.currentTarget as HTMLElement).blur();
  };

  // −/+ са antd SVG икони (не текстови глифи — те висят под центъра заради
  // baseline-а на шрифта); grid place-items-center ги центрира геометрично,
  // както в степерите на панела/сторно/раздели.
  // 36×36 — този степер е само в мобилния (stacked) ред; десктопът ползва
  // компактния CatalogQtyStepper с молива.
  const stepperBtn =
    "w-9 h-9 grid place-items-center p-0 bg-pos-stepper cursor-pointer";
  // Минусът е блед, докато няма какво да маха
  const stepperIcon = "text-[13px]";

  return (
    <div
      ref={ref}
      onClick={handleClick}
      // Фонът в цвета на станцията/групата идва от данните → inline style;
      // маркираният ред е по-наситен. Без цвят — старите pos фонове.
      // Изчерпан обеден продукт остава сив (цветът би бил bg-pos-line2).
      style={{
        background:
          tint && !lunchSoldOut
            ? tintBackground(tint, hasQty ? 32 : 18)
            : undefined,
      }}
      aria-disabled={lunchSoldOut || undefined}
      className={`flex items-center gap-3 cursor-pointer border ${
        stacked ? "px-3 py-[7px]" : "px-3.5 py-1"
      } ${lunchSoldOut ? "bg-pos-line2" : hasQty ? "bg-pos-marked" : "bg-pos-card"} ${
        selected
          ? // Курсорът (клавиатурният фокус) е ЗАОБЛЕН контур в мастило, рисуван
            // от ::after върху реда: фонът на реда остава прав (нищо бяло не
            // прозира по ъглите), а контурът е с радиус 9px — в първия/последния
            // ред следва извивката на картата-група (13 = 14px − 1px рамка), за
            // да не го реже overflow-hidden. Разделителят остава.
            // Цветът на контура: без оцветяване — кехлибарен (както преди);
            // при оцветен ред — мастило, което стои върху всеки цвят.
            `relative after:pointer-events-none after:absolute after:inset-0 after:rounded-[9px] first:after:rounded-t-[13px] last:after:rounded-b-[13px] after:border ${
              tint ? "after:border-pos-ink" : "after:border-pos-accent"
            } border-transparent border-b-pos-rowline last:border-b-transparent`
          : // Разделителят между редовете е долната рамка; последният ред е без
            // него.
            "border-transparent border-b-pos-rowline last:border-b-transparent"
      }`}
    >
      {/* Лява акцентна лента — видима само при маркирано количество */}
      <span
        aria-hidden
        className={`w-1 shrink-0 rounded-[3px] ${stacked ? "h-7" : "h-6"} ${
          hasQty ? "bg-pos-accent" : "bg-transparent"
        }`}
      />

      {/* Име (+ цена под него в stacked варианта); единица/размер — отделна
          дясно-подравнена колонка преди цената, за да се подравняват вертикално */}
      {stacked ? (
        <span className="flex-1 min-w-0 flex flex-col text-left">
          <span
            className={`text-[13.5px] font-semibold leading-tight ${lunchSoldOut ? "text-pos-muted" : "text-pos-ink"}`}
          >
            {highlightMatch(itemName)}
            {isLunchItem(item) && (
              <LunchTag inline soldOut={lunchSoldOut} className="ml-1.5" />
            )}
            {item.pluCode != null && (
              <span className="font-pos-mono text-[11px] font-medium text-pos-muted">
                {" "}
                | {highlightPluCode(item.pluCode, searchTerm)}
              </span>
            )}
          </span>
          <span className="font-pos-mono text-[11px] text-pos-muted">
            {showAdjustedPrice ? (
              <>
                <span className="line-through opacity-70">
                  {item.price.toFixed(2)}
                </span>{" "}
                <span className="font-semibold text-pos-disc-ink">
                  {formatSignedBillPercent(billDiscountPercent)}
                </span>{" "}
                <span className="font-semibold text-pos-ink">
                  {adjustedPrice.toFixed(2)} €
                </span>
              </>
            ) : (
              <>{item.price.toFixed(2)} €</>
            )}
            {lunchRemaining != null && (
              <>
                {" · "}
                <LunchCount count={lunchRemaining} />
              </>
            )}
            {unitAndSize ? ` · ${unitAndSize}` : ""}
          </span>
        </span>
      ) : (
        <>
          <span
            className={`text-base font-semibold flex-1 min-w-0 text-left ${lunchSoldOut ? "text-pos-muted" : "text-pos-ink"}`}
          >
            {highlightMatch(itemName)}
            {item.pluCode != null && (
              <span className="font-pos-mono text-[14px] font-medium text-pos-muted">
                {" "}
                | {highlightPluCode(item.pluCode, searchTerm)}
              </span>
            )}
          </span>
          {/* „Обедно" / „Изчерпано" — в своя колона между името и степера */}
          {isLunchItem(item) && <LunchTag soldOut={lunchSoldOut} />}
          {/* Степер − N + | молив — само при маркирано количество (появява се
              с клика по реда, който добавя +1) */}
          {hasQty && (
            <CatalogQtyStepper
              quantity={quantity}
              onDecrement={() =>
                onQuantityChange?.(item.id, Math.max(0, quantity - 1))
              }
              onIncrement={() => onQuantityChange?.(item.id, quantity + 1)}
              onEdit={() => onEdit?.(item.id)}
              incrementDimmed={lunchLeft === 0}
            />
          )}
          {/* Остават „N бр" | грамаж — колона с минимална ширина, за да стоят
              степерите в колона по редовете (и при редове без грамаж); при
              обеден продукт е по-широка, за да побере и остатъка */}
          <span
            className={`shrink-0 flex items-center justify-end gap-3 font-pos-mono text-base ${
              isLunchItem(item) ? "min-w-[150px]" : "min-w-[64px]"
            }`}
          >
            {lunchRemaining != null && <LunchCount count={lunchRemaining} />}
            {lunchRemaining != null && unitAndSize && (
              <span aria-hidden className="shrink-0 w-px h-4 bg-pos-ctl" />
            )}
            {unitAndSize && (
              <span className="font-medium text-pos-muted whitespace-nowrap text-right">
                {unitAndSize}
              </span>
            )}
          </span>
          {/* Колона „Станция" — chip с точка в цвета ѝ, висок колкото
              степера; минимална ширина и дясно подравняване, за да стоят в
              колона по редовете */}
          {station && (
            <span className="shrink-0 min-w-[88px] max-w-[150px] flex justify-end">
              <span className="inline-flex h-8 max-w-full items-center gap-1.5 rounded-[7px] border border-pos-line3 bg-pos-card px-2 text-[12.5px] font-semibold text-pos-soft">
                <span
                  aria-hidden
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ background: station.color ?? undefined }}
                />
                <span className="truncate">{station.name}</span>
              </span>
            </span>
          )}
          {billDiscountPercent !== 0 ? (
            /* Фиксирани мини-ширини + дясно подравняване — стара цена, % и
               нова цена стоят в колони по редовете, не назъбено по съдържание.
               Групата се рендира и за редове без корекция (заключена цена) с
               празни първи две клетки — иначе тясната им ценова клетка
               измества останалите колони спрямо другите редове. */
            <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap">
              <span className="min-w-[44px] text-right font-pos-mono text-sm text-pos-muted line-through opacity-70">
                {showAdjustedPrice ? item.price.toFixed(2) : ""}
              </span>
              <span className="min-w-[38px] text-right text-[13px] font-semibold text-pos-disc-ink">
                {showAdjustedPrice
                  ? formatSignedBillPercent(billDiscountPercent)
                  : ""}
              </span>
              <span className="min-w-[88px] text-right font-pos-mono text-base font-semibold text-pos-ink2">
                {(showAdjustedPrice ? adjustedPrice : item.price).toFixed(2)} €
              </span>
            </span>
          ) : (
            /* Цената е последна — минимална ширина, дясно подравнена, за да
               стоят станциите в колона и при различно дълги цени */
            <span
              className={`min-w-[88px] text-right font-pos-mono text-base font-semibold whitespace-nowrap ${
                lunchSoldOut ? "text-pos-muted" : "text-pos-ink2"
              }`}
            >
              {item.price.toFixed(2)} €
            </span>
          )}
        </>
      )}

      {/* Степер − N + (само телефон — на десктоп е компактният с молива) */}
      {/* onMouseDown preventDefault — да НЕ открадне фокуса от търсачката на
          телефон (иначе клавиатурата се затваря при всяко +/−); кликът си
          минава нормално. */}
      {stacked && (
        <span className="inline-flex items-stretch border border-pos-ctl overflow-hidden bg-pos-card shrink-0 rounded-[9px]">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleDecrement}
            className={`${stepperBtn} text-pos-dim ${hasQty ? "" : "opacity-40"}`}
          >
            <MinusOutlined className={stepperIcon} />
          </button>
          <span
            className="min-w-[34px] grid place-items-center font-pos-mono font-semibold text-pos-ink text-[15px]"
          >
            {quantity}
          </span>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleIncrement}
            className={`${stepperBtn} text-pos-accent-ink ${lunchLeft === 0 ? "opacity-40" : ""}`}
          >
            <PlusOutlined className={stepperIcon} />
          </button>
        </span>
      )}
    </div>
  );
};
